// @flow

import queryString from 'query-string'

import {
  type DeviceType,
  type Wizard,
  type MetricNameType,
  type EventNameType,
  type EventDataType,
  type WizardPageData,
  type CustomerConfig,
  type IndividualProductData
} from '@chatchamp/chatchamp-types'

import { type ApiParamsType, httpCall } from '../http'
import { device } from '../device'

export class ChatchampApi {
  customerId: string

  constructor (customerId: string) {
    this.customerId = customerId
  }

  getLinkPreview (url: string): Promise<Object> {
    return apiCall(this.customerId, `link-preview/metadata?url=${url}`, { method: 'get' })
  }
}

function apiCall (customerId: string, path: string, params: ApiParamsType): Promise<any> {
  if (!customerId) throw new Error('Customer id has to be set.')

  const url = new URL(`${process.env.CHATCHAMP_API}/${path}`)
  url.searchParams.set('customerId', customerId)

  return httpCall({
    ...params,
    url: url.toString(),
    retries: 3
  })
}

export function getCustomerConfig (customerId: string, subscriberId: string = '', query: string = ''): Promise<CustomerConfig> {
  return apiCall(customerId, `channels/${customerId}/subscribers/${subscriberId}?${query}`, { method: 'get' })
}

export function createGrowthImpression (customerId: string, subscriberId: string, welcomeMessageId: number, parentUrl: string, device: DeviceType): any {
  if (!welcomeMessageId) throw new Error('Welcome message id has to be passed.')

  return apiCall(customerId, 'growthImpressions', {
    method: 'post',
    body: {
      growthToolId: welcomeMessageId,
      chatchampSessionId: subscriberId,
      referrer: parentUrl,
      device
    }
  })
}

export function getSubscriber (customerId: string, subscriberId: string): Promise<any> {
  return apiCall(customerId, `subscribers/${subscriberId}`, { method: 'get' })
}

// Dominik; 13.07.2021; backwards compatibility for deprecated API
function backwardsCompatibilityForDeprecatedApi (body: Object, params: any): Object {
  if (Array.isArray(params)) {
    return {
      ...body,
      products: params
    }
  } else {
    return {
      ...body,
      ...params
    }
  }
}

export function createOrder (customerId: string, subscriberId: string, params: any = {}): Promise<any> {
  const body: Object = {
    chatchampSessionId: subscriberId,
    products: [],
    total: null
  }

  return apiCall(customerId, 'orders', {
    method: 'post',
    body: backwardsCompatibilityForDeprecatedApi(body, params)
  })
}

export function createWebsiteEvent (customerId: string, subscriberId: string, name: string, params: any = {}): Promise<any> {
  const body: Object = {
    chatchampSessionId: subscriberId,
    name: name,
    products: []
  }

  return apiCall(customerId, 'website_events', {
    method: 'post',
    body: backwardsCompatibilityForDeprecatedApi(body, params)
  })
}

export function updateSubscriber (customerId: string, subscriberId: string, attributes: Object, options?: {
  createAttributesIfNotExist: boolean
} = { createAttributesIfNotExist: true }): any {
  return apiCall(customerId, `subscribers/${subscriberId}`, {
    method: 'put',
    body: {
      attributes,
      options
    }
  })
}

export function updateSubscriberProducts (customerId: string, subscriberId: string, products: Array<IndividualProductData>): Promise<any> {
  return apiCall(customerId, `subscribers/${subscriberId}/product_attributes`, {
    method: 'put',
    body: {
      products
    }
  })
}

export function createClick (customerId: string, subscriberId: string, buttonPayload: string, params: Object = {}): any {
  return apiCall(customerId, `subscribers/${subscriberId}/clicks/${buttonPayload}`, {
    method: 'post',
    body: params
  })
}

export function createVisit (customerId: string, subscriberId: string, uri: string): Promise<any> {
  return apiCall(customerId, 'visits', {
    method: 'post',
    body: {
      chatchampSessionId: subscriberId,
      uri: uri
    }
  })
}

export const sendSpecialChatbot = (customerId: string, subscriberId: string, chatbotType: string): any => {
  return apiCall(customerId, `subscribers/${subscriberId}/specialChatbots/${chatbotType}`, {
    method: 'post',
    body: {}
  })
}

// TODO; Dominik; 01.02.2023; atm the api requires a message, this is not really necessary since there can be links
//  that are not associated to messages (e.g. headlines)
export function createClickout (customerId: string, subscriberId: string, messageId: number, params: Object): Promise<any> {
  return apiCall(customerId, 'clickouts', {
    method: 'post',
    body: Object.assign({
      chatchampSessionId: subscriberId,
      messageId: messageId
    }, params)
  })
}

export function createMetricEvent (customerId: string, subscriberId: string, metricName: MetricNameType, eventName: EventNameType, eventData: EventDataType = {}, eventCreatedAt: Date = new Date()): Promise<any> {
  const defaultEventData: EventDataType = { device: device() }

  return apiCall(customerId, 'metric_events', {
    method: 'post',
    body: {
      chatchampSessionId: subscriberId,
      metricName,
      eventName,
      eventData: Object.assign(defaultEventData, eventData),
      eventCreatedAt: eventCreatedAt.toISOString()
    }
  })
}

export function getWizard (customerId: string, subscriberId: string, integrationId: string): Promise<Wizard> {
  const query = queryString.stringify({ chatchampSessionId: subscriberId })

  return apiCall(customerId, `wizards/${integrationId}?${query}`, { method: 'get' })
}

export function getWizardPages (customerId: string, wizardPayload: string): Promise<Array<any>> {
  if (!wizardPayload) throw new Error('Need to pass a wizard payload.')

  return apiCall(customerId, `wizards/${wizardPayload}/pages`, { method: 'get' })
}

export function getWizardPage (customerId: string, subscriberId: string, wizardPayload: string, pageId: string): Promise<WizardPageData<any>> {
  const query = queryString.stringify({ chatchampSessionId: subscriberId })

  return apiCall(customerId, `wizards/${wizardPayload}/pages/${pageId}?${query}`, { method: 'get' })
}

export function saveAndGetNextPage (customerId: string, subscriberId: string, wizardPayload: string, pageId: string, data: any): Promise<WizardPageData<any>> {
  const query = queryString.stringify({ chatchampSessionId: subscriberId })
  const body = { options: data }

  return apiCall(customerId, `wizards/${wizardPayload}/pages/${pageId}?${query}`, {
    method: 'post',
    body
  })
}

export function getUploadFileUrl (customerId: string, subscriberId: string = '', fileName: string, contentType: string, folder: string = ''): Promise<Object> {
  const query = queryString.stringify({
    fileName,
    contentType,
    folder
  })
  return apiCall(customerId, `subscribers/${subscriberId}/uploadFileUrl?${query}`, { method: 'get' })
}

export function deleteFile (customerId: string, subscriberId: string = '', fileKey: string = ''): Promise<any> {
  const query = queryString.stringify({ fileKey })
  return apiCall(customerId, `subscribers/${subscriberId}/deleteFile?${query}`, { method: 'delete' })
}

export function getRecommendedProducts (customerId: string, subscriberId: string, params: any = {}): Promise<any> {
  const query = queryString.stringify(params, { arrayFormat: 'bracket' })
  return apiCall(customerId, `recommender/${subscriberId}?${query}`, {
    method: 'get'
  })
}
